import { useTheme } from '@/hooks/useTheme';
import { useTranslation } from 'react-i18next';
import { OwnerStation, ownerStatusColor, shortStationName } from '@/mocks/ownerDashboard';
import { OwnerKpis } from '@/hooks/useOwnerDashboard';

interface Props {
  kpis: OwnerKpis | null;
  stations: OwnerStation[];
  loading?: boolean;
  /** Technical-status / generation tiles -> per-SES asset detail (technical tab). */
  onStationDetail?: (s: OwnerStation) => void;
  /** Forecast tile -> /forecast with that SES selected in the topbar. */
  onStationForecast?: (s: OwnerStation) => void;
}

// Dispatcher KPI row — same layout as the owner HeroKpiRow but purely TECHNICAL
// (no money/markets): current generation, day forecast, plant status, forecast
// fulfilment. Rows share one canonical order so they line up across all 4 tiles.
export default function DispatcherKpiRow({ kpis, stations, loading, onStationDetail, onStationForecast }: Props) {
  const { isDark } = useTheme();
  const { t } = useTranslation();

  const cardBg = isDark ? '#1A1A1A' : '#FFFFFF';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = '#6B6B70';
  const textDim = isDark ? '#4A4A50' : '#9B9BA4';
  const rowBorder = isDark ? 'rgba(255,255,255,0.05)' : '#F0F0F2';
  const hoverBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';

  const dash = loading ? '…' : '—';
  const workingCount = kpis?.workingCount ?? stations.filter((s) => s.status === 'green' || s.status === 'amber').length;
  const totalCount = kpis?.totalCount ?? stations.length;

  const liveStations = stations.filter((s) => s.status !== 'pre_launch');
  const avgPct = liveStations.length
    ? Math.round(liveStations.reduce((x, s) => x + (s.percentOfPotential ?? 0), 0) / liveStations.length)
    : null;

  const orderedStations = [...stations].sort((a, b) => {
    const ap = a.status === 'pre_launch' ? 1 : 0;
    const bp = b.status === 'pre_launch' ? 1 : 0;
    if (ap !== bp) return ap - bp;
    return (b.installedMw ?? 0) - (a.installedMw ?? 0);
  });

  type Metric = { get: (s: OwnerStation) => number | null | undefined; fmt: (v: number) => string };
  const breakdown = (m: Metric, onRowClick?: (s: OwnerStation) => void) => {
    const rows = orderedStations.map((s) => ({ s, v: m.get(s) }));
    const clickable = !!onRowClick;
    return (
      <div className="mt-3 pt-2.5 space-y-0.5" style={{ borderTop: `1px solid ${cardBorder}` }}>
        {rows.map(({ s, v }) => (
          <div key={s.id}
               onClick={clickable ? () => onRowClick!(s) : undefined}
               role={clickable ? 'button' : undefined}
               tabIndex={clickable ? 0 : undefined}
               className={`flex items-center justify-between gap-2 py-0.5 rounded ${clickable ? 'cursor-pointer -mx-1.5 px-1.5' : ''}`}
               style={{ borderBottom: `1px solid ${rowBorder}` }}
               onMouseEnter={clickable ? (e) => { e.currentTarget.style.background = hoverBg; } : undefined}
               onMouseLeave={clickable ? (e) => { e.currentTarget.style.background = 'transparent'; } : undefined}>
            <span className="text-[11px] truncate flex items-center gap-1.5" style={{ color: textMuted }}>
              <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: ownerStatusColor(s.status) }} />
              {shortStationName(s.name)}
            </span>
            <span className="text-[11px] font-semibold tabular-nums shrink-0 flex items-center gap-1"
                  style={{ color: s.status === 'pre_launch' ? textDim : textMain }}>
              {s.status === 'pre_launch' || v == null ? '—' : m.fmt(v)}
              {clickable && <i className="ri-arrow-right-s-line" style={{ color: textDim, fontSize: 13 }} />}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const Tile = ({ children }: { children: React.ReactNode }) => (
    <div className="rounded-xl p-4 flex flex-col h-full" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
      {children}
    </div>
  );

  const Head = ({ label, icon, accent }: { label: string; icon: string; accent: string }) => (
    <div className="flex items-start justify-between mb-2.5">
      <div className="text-[10px] font-bold tracking-widest uppercase" style={{ color: textMuted, fontFamily: 'Muller, Inter, sans-serif' }}>{label}</div>
      <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ background: `${accent}15` }}>
        <i className={icon} style={{ color: accent, fontSize: 14 }} />
      </div>
    </div>
  );

  const Value = ({ text, color }: { text: string; color: string }) => (
    <span className="text-base font-bold tabular-nums leading-tight block"
          style={{ color, letterSpacing: '-0.02em', fontFamily: 'Muller, Inter, sans-serif' }}>{text}</span>
  );

  const Note = ({ text }: { text: string }) => (
    <div className="text-[11px] mt-0.5" style={{ color: textMuted, fontFamily: 'Muller, Inter, sans-serif' }}>{text}</div>
  );

  const Caption = ({ text }: { text: string }) => (
    <div className="text-[10px] mt-0.5" style={{ color: textDim }}>{text}</div>
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5 items-stretch">

      {/* 1 — Current generation (MW) */}
      <Tile>
        <Head label={t('d_dkpi_cur_gen')} icon="ri-flashlight-line" accent="#22C55E" />
        <Value text={kpis?.factNowMw != null ? `${kpis.factNowMw.toFixed(2)} ${t('d_u_mw')}` : dash} color="#22C55E" />
        <Note text={t('d_dkpi_total_power_now')} />
        <Caption text={t('d_dkpi_modbus_src')} />
        {kpis && breakdown({ get: (s) => s.nowPacKw, fmt: (v) => `${(v / 1000).toFixed(2)} ${t('d_u_mw')}` }, onStationDetail)}
      </Tile>

      {/* 2 — Forecast today (MWh) */}
      <Tile>
        <Head label={t('d_kpi_forecast_today')} icon="ri-sun-line" accent="#1F5B8F" />
        <Value text={kpis?.forecastTodayMwh != null ? `${kpis.forecastTodayMwh.toFixed(1)} ${t('d_u_mwh')}` : dash} color="#1F5B8F" />
        <Note text={t('d_kpi_expected_gen')} />
        <Caption text={t('d_dkpi_model_weather')} />
        {kpis && breakdown({ get: (s) => s.todayForecastMwh, fmt: (v) => `${v.toFixed(1)}` }, onStationForecast)}
      </Tile>

      {/* 3 — Plant status */}
      <Tile>
        <Head label={t('d_kpi_tech_status')} icon="ri-pulse-line" accent="#0D7A3C" />
        <Value text={`${workingCount}/${totalCount} ${t('d_kpi_generating')}`} color={workingCount === totalCount ? '#22C55E' : textMain} />
        <Note text={t('d_kpi_current_status')} />
        <Caption text={t('d_kpi_updates_auto')} />
        <div className="mt-3 pt-2.5 space-y-0.5 flex-1" style={{ borderTop: `1px solid ${cardBorder}` }}>
          {orderedStations.map((s) => {
            const clickable = !!onStationDetail;
            return (
            <div key={s.id}
                 onClick={clickable ? () => onStationDetail!(s) : undefined}
                 role={clickable ? 'button' : undefined}
                 tabIndex={clickable ? 0 : undefined}
                 className={`flex items-center justify-between gap-2 py-0.5 rounded ${clickable ? 'cursor-pointer -mx-1.5 px-1.5' : ''}`}
                 style={{ borderBottom: `1px solid ${rowBorder}` }}
                 onMouseEnter={clickable ? (e) => { e.currentTarget.style.background = hoverBg; } : undefined}
                 onMouseLeave={clickable ? (e) => { e.currentTarget.style.background = 'transparent'; } : undefined}>
              <span className="text-[11px] truncate flex items-center gap-1.5" style={{ color: textMain }}>
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: ownerStatusColor(s.status) }} />
                {shortStationName(s.name)}
              </span>
              <span className="text-[10px] font-medium shrink-0 text-right flex items-center gap-1" style={{ color: ownerStatusColor(s.status) }}>
                {s.statusLabel}
                {clickable && <i className="ri-arrow-right-s-line" style={{ color: textDim, fontSize: 13 }} />}
              </span>
            </div>
          );})}
        </div>
      </Tile>

      {/* 4 — Forecast fulfilment (%) */}
      <Tile>
        <Head label={t('d_dkpi_fulfilment')} icon="ri-focus-3-line" accent="#E0A030" />
        <Value text={avgPct != null ? `${avgPct}%` : dash} color="#E0A030" />
        <Note text={t('d_dkpi_fact_fc_avg')} />
        <Caption text={t('d_dkpi_portfolio_eff')} />
        {kpis && breakdown({ get: (s) => s.percentOfPotential, fmt: (v) => `${v}%` }, onStationForecast)}
      </Tile>

    </div>
  );
}
